import {
  generateSampleNumber,
  newSample,
  Sample,
  updateSampleDocument,
  updateSampleLab,
  updateSampleNumber,
} from "@/services/backend/samples/types/sample";
import { GrowthMedium } from "@/services/backend/samples/types/growth-medium";
import { Kind } from "@/services/backend/samples/types/kind";
import { v4 } from "uuid";
import { EntityId } from "@/shared/types/entity-id";
import { Namespace } from "@/shared/types/namespace";
import { AirMicrobialSampleSet } from "@/services/backend/samples/types/air-microbial-sample";
import { MoldDifferentiation } from "@/services/backend/samples/types/mold-differentiation";

export interface SurfaceMicrobialSampleSet {
  id: string;
  documentId: EntityId;
  documentNamespace: Namespace | null;
  labId: EntityId;
  number: string;
  location: string;
  spot: string;
  comment: string;
  sampleTakers: string;
  value: string;
  unit: string;
  caso: SurfaceMicrobialSample | null;
  malt: SurfaceMicrobialSample | null;
  dg18: SurfaceMicrobialSample | null;
  moldDifferentiation: MoldDifferentiation[];
}

export function newSurfaceMicrobialSampleSet(
  id: string,
  documentId: EntityId,
  documentNamespace: Namespace | null,
  location: string,
  spot: string,
): SurfaceMicrobialSampleSet {
  const number = generateSampleNumber();
  return {
    id,
    documentId,
    documentNamespace,
    labId: null,
    number,
    location,
    spot,
    comment: "",
    sampleTakers: "",
    value: "",
    unit: "KBE/25cm²",
    // caso and malt are always present, dg18 is optional
    caso: {
      ...newSurfaceMicrobialSample(
        v4(),
        `${number}.CASO`,
        GrowthMedium.CASO,
        documentId,
        documentNamespace,
      ),
      location,
      spot,
    },
    malt: {
      ...newSurfaceMicrobialSample(
        v4(),
        `${number}.MALT`,
        GrowthMedium.MALT,
        documentId,
        documentNamespace,
      ),
      location,
      spot,
    },
    dg18: null,
    moldDifferentiation: [],
  };
}

export function updateDocument(
  set: AirMicrobialSampleSet,
  id: EntityId,
  namespace: Namespace,
): AirMicrobialSampleSet {
  return {
    ...set,
    documentId: id,
    documentNamespace: namespace,
    caso: set.caso ? updateSampleDocument(set.caso, id, namespace) : null,
    malt: set.malt ? updateSampleDocument(set.malt, id, namespace) : null,
  };
}

export function updateSurfaceMicrobialSetNumber(
  set: SurfaceMicrobialSampleSet,
  number: string,
): SurfaceMicrobialSampleSet {
  return {
    ...set,
    number,
    caso: set.caso ? updateSampleNumber(set.caso, `${number}-CASO`) : null,
    malt: set.malt ? updateSampleNumber(set.malt, `${number}-MALT`) : null,
    dg18: set.dg18 ? updateSampleNumber(set.dg18, `${number}-DG18`) : null,
  };
}

export function updateCreatedAtInSurfaceSet(
  sample: SurfaceMicrobialSampleSet,
  createdAt: string | null,
): SurfaceMicrobialSampleSet {
  return {
    ...sample,
    caso: sample.caso ? { ...sample.caso, createdAt } : sample.caso,
    malt: sample.malt ? { ...sample.malt, createdAt } : sample.malt,
    dg18: sample.dg18 ? { ...sample.dg18, createdAt } : sample.dg18,
  };
}

export function updateSurfaceMicrobialSetLab(
  set: SurfaceMicrobialSampleSet,
  labId: EntityId,
): SurfaceMicrobialSampleSet {
  return {
    ...set,
    labId,
    caso: set.caso ? updateSampleLab(set.caso, labId) : null,
    malt: set.malt ? updateSampleLab(set.malt, labId) : null,
    dg18: set.dg18 ? updateSampleLab(set.dg18, labId) : null,
  };
}

export function updateSurfaceMicrobialSetComment(
  set: SurfaceMicrobialSampleSet,
  comment: string,
): SurfaceMicrobialSampleSet {
  return {
    ...set,
    comment,
  };
}

export function updateSurfaceMicrobialSetCaso(
  set: SurfaceMicrobialSampleSet,
  sample: SurfaceMicrobialSample,
): SurfaceMicrobialSampleSet {
  if (sample.growthMedium !== GrowthMedium.CASO) {
    return set;
  }

  return {
    ...set,
    caso: sample,
  };
}

export function updateSurfaceMicrobialSetMalt(
  set: SurfaceMicrobialSampleSet,
  sample: SurfaceMicrobialSample,
): SurfaceMicrobialSampleSet {
  if (sample.growthMedium !== GrowthMedium.MALT) {
    return set;
  }

  return {
    ...set,
    malt: sample,
  };
}

export function updateSurfaceMicrobialSetDg18(
  set: SurfaceMicrobialSampleSet,
  sample: SurfaceMicrobialSample,
): SurfaceMicrobialSampleSet {
  if (sample.growthMedium !== GrowthMedium.DG18) {
    return set;
  }

  return {
    ...set,
    dg18: sample,
  };
}

export function activateSurfaceMicrobialSetSample(
  set: SurfaceMicrobialSampleSet,
  growthMedium: GrowthMedium,
): SurfaceMicrobialSampleSet {
  switch (growthMedium) {
    case GrowthMedium.CASO:
      return {
        ...set,
        caso: set.caso ?? {
          ...newSurfaceMicrobialSample(
            v4(),
            `${set.number}.CASO`,
            GrowthMedium.CASO,
            set.documentId,
            set.documentNamespace,
          ),
          location: set.location,
          spot: set.spot,
          labId: set.labId,
        },
      };
    case GrowthMedium.MALT:
      return {
        ...set,
        malt: set.malt ?? {
          ...newSurfaceMicrobialSample(
            v4(),
            `${set.number}.MALT`,
            GrowthMedium.MALT,
            set.documentId,
            set.documentNamespace,
          ),
          location: set.location,
          spot: set.spot,
          labId: set.labId,
        },
      };
    case GrowthMedium.DG18:
      return {
        ...set,
        dg18: set.dg18 ?? {
          ...newSurfaceMicrobialSample(
            v4(),
            `${set.number}.DG18`,
            GrowthMedium.DG18,
            set.documentId,
            set.documentNamespace,
          ),
          location: set.location,
          spot: set.spot,
          labId: set.labId,
        },
      };
    default:
      return set;
  }
}

export function deactivateSurfaceMicrobialSetSample(
  set: SurfaceMicrobialSampleSet,
  growthMedium: GrowthMedium,
): SurfaceMicrobialSampleSet {
  switch (growthMedium) {
    case GrowthMedium.CASO:
      return {
        ...set,
        caso: null,
      };
    case GrowthMedium.MALT:
      return {
        ...set,
        malt: null,
      };
    case GrowthMedium.DG18:
      return {
        ...set,
        dg18: null,
      };
    default:
      return set;
  }
}

export function surfaceMicrobialSampleIsActivated(
  set: SurfaceMicrobialSampleSet,
  growthMedium: GrowthMedium,
): boolean {
  switch (growthMedium) {
    case GrowthMedium.CASO:
      return !!set.caso;
    case GrowthMedium.MALT:
      return !!set.malt;
    case GrowthMedium.DG18:
      return !!set.dg18;
    default:
      return false;
  }
}

export interface SurfaceMicrobialSample extends Sample {
  growthMedium: GrowthMedium;
  value: string;
  unit: string;
}

function newSurfaceMicrobialSample(
  id: string,
  number: string,
  growthMedium: GrowthMedium,
  documentId: EntityId,
  documentNamespace: Namespace | null,
): SurfaceMicrobialSample {
  const sample = newSample(
    id,
    number,
    Kind.SurfaceMicrobial,
    documentId,
    documentNamespace,
  );

  return {
    ...sample,
    growthMedium,
    value: "",
    unit: "KBE/m²",
  };
}
